import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import api from "../../../../services/api";
import { formatDateInNewfoundland } from "../../../../utils/dates/formatDateInNewfoundland";
import { useSnackbarAlert } from "../../../../hooks/useSnackbarAlert";

const DriverShoppingListSelector = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const snackbar = useSnackbarAlert();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const res = await api.get("/admin/shopping-lists");
        const visible = res.data.filter((l) => l.status !== "merged");
        setLists(visible);
      } catch (err) {
        console.error("Error fetching lists", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLists();
  }, []);

  const now = dayjs();
  const upcoming = lists.filter((l) => dayjs(l.shoppingDate).isAfter(now));
  const past = lists.filter((l) => dayjs(l.shoppingDate).isBefore(now));

  const toggleSelect = (id, storeId) => {
    const target = lists.find((l) => l._id === id);
    const selectedLists = selectedIds.map((sid) =>
      lists.find((l) => l._id === sid)
    );

    const sameStore =
      selectedLists.every((l) => l.storeId._id === target.storeId._id) ||
      selectedIds.length === 0;

    if (!sameStore) {
      snackbar.error("All selected lists must belong to the same store.");
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleMerge = async () => {
    try {
      const res = await api.post("/admin/shopping-lists/merge", {
        listIds: selectedIds,
      });

      snackbar.success("Lists merged.");
      navigate(`/admin/driver-checklist/${res.data.mergedList._id}`);
    } catch (err) {
      console.error("Merge failed:", err);
      snackbar.error("Failed to merge lists.");
    }
  };

  if (loading) return <CircularProgress />;

  const renderList = (list) => {
    const completed = list.items.filter((i) => i.status !== "pending").length;
    const total = list.items.length;

    return (
      <ListItemButton
        key={list._id}
        onClick={() => {
          if (selectedIds.length > 0) {
            toggleSelect(list._id, list.storeId._id);
          } else {
            navigate(`/admin/driver-checklist/${list._id}`, {
              state: {
                listIds: [...upcoming, ...past].map((l) => l._id),
                currentIndex: [...upcoming, ...past].findIndex(
                  (l) => l._id === list._id
                ),
              },
            });
          }
        }}
        sx={{
          borderBottom: "1px solid #eee",
          flexDirection: "column",
          alignItems: "flex-start",
          py: 2,
          backgroundColor: selectedIds.includes(list._id)
            ? "#f5f5ff"
            : "inherit",
          "&:hover": {
            backgroundColor: selectedIds.includes(list._id)
              ? "#f0f4ff"
              : "rgba(0,0,0,0.04)",
          },
        }}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Typography fontWeight={500}>
            {list.storeId?.displayName || "Unknown Store"}
          </Typography>
          <Chip
            size="small"
            label={selectedIds.includes(list._id) ? "Selected" : "Select"}
            variant="outlined"
            color={selectedIds.includes(list._id) ? "primary" : "default"}
            onClick={(e) => {
              e.stopPropagation(); // ✅ prevent navigation
              toggleSelect(list._id, list.storeId._id);
            }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Submitted:{" "}
          {formatDateInNewfoundland(list.createdAt, "MMM D, YYYY h:mm A")}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Shopping Date:{" "}
          {formatDateInNewfoundland(list.shoppingDate, "MMM D, YYYY")}
        </Typography>

        <Box>
          <Chip
            label={`${completed}/${total} done`}
            color={completed === total ? "success" : "default"}
            size="small"
          />
        </Box>
      </ListItemButton>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Current Grocery Lists
      </Typography>

      {(!upcoming || upcoming.length === 0) && (
        <Typography>No upcoming list</Typography>
      )}

      {upcoming.length > 0 && (
        <Paper variant="outlined">
          <List dense>{upcoming.map(renderList)}</List>
        </Paper>
      )}

      {past.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 2,
              cursor: "pointer",
            }}
            onClick={() => setShowArchived(!showArchived)}>
            <Typography variant="subtitle2" fontWeight={600}>
              {showArchived ? "Hide Past Lists" : "Show Past Lists"}
            </Typography>
            <IconButton size="small">
              {showArchived ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={showArchived} timeout="auto" unmountOnExit>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Past Lists
            </Typography>
            <Paper variant="outlined">
              <List dense>{past.map(renderList)}</List>
            </Paper>
          </Collapse>
        </>
      )}
      {selectedIds.length >= 2 && (
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 3 }}
          onClick={handleMerge}>
          Merge {selectedIds.length} Lists
        </Button>
      )}
    </Box>
  );
};

export default DriverShoppingListSelector;
